import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Clipboard } from '@capacitor/clipboard';
import { AppLauncher } from '@capacitor/app-launcher';

/**
 * Lo que se hace distinto en la web y en la app de iOS (Capacitor), en un
 * solo sitio. El resto del código llama a estas funciones y no pregunta dónde
 * está: en la web hacen lo de siempre; en la app usan las funciones nativas,
 * porque dentro de la app descargar un archivo o abrir una ventana no hace nada.
 */

/** true dentro de la app de iOS (o Android); false en cualquier navegador. */
export const esApp = Capacitor.isNativePlatform();

function blobABase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(String(lector.result).split(',')[1] ?? '');
    lector.onerror = () => reject(lector.error);
    lector.readAsDataURL(blob);
  });
}

/**
 * Entrega un archivo al usuario: en la web se descarga; en la app se abre la
 * hoja de compartir de iOS (Guardar en Archivos, AirDrop, WhatsApp, Mail…).
 * `contenido` puede ser un Blob o un data URL (como las fotos de los tickets).
 */
export async function guardarArchivo(nombre: string, contenido: Blob | string): Promise<void> {
  const blob = typeof contenido === 'string' ? await (await fetch(contenido)).blob() : contenido;

  if (!esApp) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return;
  }

  // Al caché: iOS lo limpia solo, y el archivo solo hace falta mientras se comparte
  const { uri } = await Filesystem.writeFile({
    path: nombre,
    data: await blobABase64(blob),
    directory: Directory.Cache,
  });
  await compartirNativo({ files: [uri], title: nombre });
}

/** Si se puede abrir la hoja de compartir del sistema (en la app, siempre). */
export function puedeCompartir(): boolean {
  return esApp || typeof navigator.share === 'function';
}

/**
 * Hoja de compartir del sistema con un texto (y un enlace, si lo hay).
 * Devuelve false si no hay hoja de compartir: quien llama decide qué hacer
 * entonces (copiar, abrir WhatsApp…). Cancelar cuenta como compartido.
 */
export async function compartirTexto(datos: { texto: string; titulo?: string; url?: string }): Promise<boolean> {
  if (esApp) {
    await compartirNativo({ text: datos.texto, title: datos.titulo, url: datos.url });
    return true;
  }
  if (typeof navigator.share !== 'function') return false;
  try {
    await navigator.share({ text: datos.texto, title: datos.titulo, url: datos.url });
  } catch { /* cancelado */ }
  return true;
}

async function compartirNativo(opciones: Parameters<typeof Share.share>[0]) {
  try {
    await Share.share(opciones);
  } catch (e: any) {
    // Cerrar la hoja sin elegir nada no es un error
    if (!/cancel/i.test(String(e?.message ?? e))) throw e;
  }
}

export async function copiarTexto(texto: string): Promise<void> {
  if (esApp) await Clipboard.write({ string: texto });
  else await navigator.clipboard.writeText(texto);
}

/**
 * Abre un enlace fuera de la app: en la web, en otra pestaña; en iOS, con el
 * sistema, que manda los de WhatsApp (wa.me) a WhatsApp y el resto a Safari.
 */
export async function abrirEnlaceExterno(url: string): Promise<void> {
  if (esApp) await AppLauncher.openUrl({ url });
  else window.open(url, '_blank', 'noopener');
}

/** Un PDF de jsPDF, por el mismo camino que cualquier otro archivo. */
export function guardarPDF(doc: { output(tipo: 'blob'): Blob }, nombre: string): void {
  guardarArchivo(nombre, doc.output('blob'))
    .catch(e => console.error('No se pudo guardar el PDF', e));
}
